<template>
  <div class="form">
    <div class="form-title"><span class="swt"></span>{{ title }}</div>

    <!-- single ⇄ repeat -->
    <div v-if="allowModeToggle" class="field">
      <label>ประเภท</label>
      <div class="seg">
        <button
          type="button"
          :class="{ on: mode === 'single' }"
          @click="mode = 'single'"
        >
          วันนี้วันเดียว
        </button>
        <button
          type="button"
          :class="{ on: mode === 'repeat' }"
          @click="mode = 'repeat'"
        >
          ทำซ้ำ
        </button>
      </div>
    </div>

    <!-- time range -->
    <div class="field">
      <label>ช่วงเวลา</label>
      <div class="twinputs">
        <WhTimeField :value="from" @input="setTime('from', $event)" />
        <span class="arrow">–</span>
        <WhTimeField :value="to" @input="setTime('to', $event)" />
      </div>
      <div v-if="$v.from.$dirty && $v.from.$error" class="field-err">
        เวลาเริ่มต้องอยู่ก่อนเวลาเลิก
      </div>
    </div>

    <!-- room -->
    <div class="field">
      <label>ห้องตรวจ</label>
      <div class="select-wrap">
        <select v-model="roomId" class="input native-select">
          <option :value="null">— ไม่ระบุ —</option>
          <option v-for="r in rooms" :key="r.id" :value="r.id">
            {{ r.title }}
          </option>
        </select>
        <WhIcon name="chevDown" size="16" class="select-caret" />
      </div>
    </div>

    <!-- recurrence options (repeat only) -->
    <template v-if="mode === 'repeat'">
      <div class="field">
        <label>ความถี่</label>
        <div class="seg">
          <button
            type="button"
            :class="{ on: frequency === 'weekly' }"
            @click="frequency = 'weekly'"
          >
            ทุกสัปดาห์
          </button>
          <button
            type="button"
            :class="{ on: frequency === 'monthly' }"
            @click="frequency = 'monthly'"
          >
            ทุกเดือน
          </button>
        </div>
      </div>

      <div class="field">
        <label>วันในสัปดาห์</label>
        <div class="daychips">
          <button
            v-for="w in weekdayOptions"
            :key="w.code"
            type="button"
            :class="['dchip', { on: weekdays.includes(w.code) }]"
            @click="toggleWeekday(w.code)"
          >
            {{ w.label }}
          </button>
        </div>
      </div>

      <div v-if="frequency === 'monthly'" class="field">
        <label>สัปดาห์ที่</label>
        <div class="daychips">
          <button
            v-for="n in [1, 2, 3, 4, 5]"
            :key="n"
            type="button"
            :class="['dchip', { on: setpos.includes(n) }]"
            @click="toggleSetpos(n)"
          >
            {{ n }}
          </button>
        </div>
      </div>

      <div class="field">
        <label>สิ้นสุด</label>
        <div class="seg">
          <button
            type="button"
            :class="{ on: endMode === 'never' }"
            @click="endMode = 'never'"
          >
            ไม่มีกำหนด
          </button>
          <button
            type="button"
            :class="{ on: endMode === 'until' }"
            @click="endMode = 'until'"
          >
            ถึงวันที่
          </button>
        </div>
        <input
          v-if="endMode === 'until'"
          v-model="until"
          type="date"
          :class="['input', { invalid: !untilValid }]"
          style="margin-top: 8px;"
        />
      </div>
    </template>

    <!-- note -->
    <div class="field">
      <label>หมายเหตุ</label>
      <textarea
        v-model="note"
        class="input"
        maxlength="255"
        placeholder="เพิ่มหมายเหตุ (ถ้ามี)"
      ></textarea>
    </div>

    <div class="form-foot">
      <button class="qbtn" @click="$emit('cancel')">ยกเลิก</button>
      <button class="qbtn solid" :disabled="!canConfirm" @click="confirm">
        {{ confirmLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import { required, helpers } from "vuelidate/lib/validators";
import { WEEKDAYS } from "@/utils/recurrence";
import WhIcon from "./WhIcon.vue";
import WhTimeField from "./WhTimeField.vue";

const before = (otherField) =>
  helpers.withParams({ type: "before" }, function (val) {
    if (!val || !this[otherField]) return true;
    return val < this[otherField];
  });

export default {
  name: "ShiftForm",
  components: { WhIcon, WhTimeField },
  props: {
    title: { type: String, default: "เพิ่มเวลาทำงาน" },
    confirmLabel: { type: String, default: "บันทึก" },
    defaultFrom: { type: String, default: "09:00" },
    defaultTo: { type: String, default: "17:00" },
    rooms: { type: Array, default: () => [] },
    // Weekday codes to pre-check in repeat mode (e.g. from the selected dates).
    presetWeekdays: { type: Array, default: () => [] },
    // false locks the mode (editing an existing single entry or rule).
    allowModeToggle: { type: Boolean, default: true },
    // Prefill for editing: { mode, from, to, roomId, note, frequency, weekdays, setpos, until }
    initial: { type: Object, default: null },
  },
  data() {
    const i = this.initial || {};
    return {
      mode: i.mode || (this.allowModeToggle ? "single" : "single"),
      frequency: i.frequency || "weekly",
      weekdays: i.weekdays || [...this.presetWeekdays],
      setpos: i.setpos || [],
      endMode: i.until ? "until" : "never",
      until: i.until || "",
      from: i.from || this.defaultFrom,
      to: i.to || this.defaultTo,
      roomId: i.roomId != null ? i.roomId : null,
      note: i.note || "",
      weekdayOptions: WEEKDAYS,
    };
  },
  validations: {
    from: { required, before: before("to") },
    to: { required },
  },
  computed: {
    untilValid() {
      if (this.endMode !== "until") return true;
      return !!this.until && (!this.from || this.until >= this.todayIso);
    },
    todayIso() {
      // Used only as a soft lower bound for the end date.
      return new Date().toISOString().slice(0, 10);
    },
    canConfirm() {
      if (this.$v.$invalid) return false;
      if (this.mode === "repeat") {
        if (this.weekdays.length === 0) return false;
        if (this.frequency === "monthly" && this.setpos.length === 0)
          return false;
        if (this.endMode === "until" && !this.until) return false;
      }
      return true;
    },
  },
  methods: {
    toggleWeekday(code) {
      const i = this.weekdays.indexOf(code);
      if (i === -1) this.weekdays.push(code);
      else this.weekdays.splice(i, 1);
    },
    toggleSetpos(n) {
      const i = this.setpos.indexOf(n);
      if (i === -1) this.setpos.push(n);
      else this.setpos.splice(i, 1);
    },
    setTime(field, val) {
      this[field] = val;
      this.$v[field].$touch();
      this.emitPreview();
    },
    emitPreview() {
      // Reuse the existing live-preview channel (shift = additive slot).
      this.$emit("preview-update", {
        type: "shift",
        from: this.from,
        to: this.to,
        roomId: this.roomId,
      });
    },
    confirm() {
      this.$v.$touch();
      if (!this.canConfirm) return;
      this.$emit("confirm", {
        mode: this.mode,
        from: this.from,
        to: this.to,
        roomId: this.roomId,
        note: this.note,
        frequency: this.frequency,
        weekdays: [...this.weekdays],
        setpos: this.frequency === "monthly" ? [...this.setpos] : [],
        until: this.endMode === "until" ? this.until : null,
      });
    },
  },
  mounted() {
    if (this.mode === "single") this.emitPreview();
  },
  beforeDestroy() {
    this.$emit("preview-update", null);
  },
};
</script>

<style scoped>
.form {
  border: 1px solid var(--line);
  background: var(--surface);
  border-radius: var(--r);
  padding: 13px;
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.form-title {
  font-size: 14.5px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 9px;
}
.form-title .swt {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--brand);
}
.field label {
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  display: block;
  margin-bottom: 5px;
}
.field-err {
  font-size: 11.5px;
  color: var(--leave);
  margin-top: 6px;
}
.twinputs {
  display: flex;
  align-items: center;
  gap: 8px;
}
.arrow {
  color: var(--faint);
}

/* inputs */
.input {
  font-family: var(--wh-num);
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  width: 100%;
  border: 1px solid var(--line-strong);
  background: var(--surface);
  border-radius: var(--r-sm);
  padding: 8px 11px;
  outline: none;
  transition: 0.14s;
}
.input:focus {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px var(--brand-tint);
}
.input.invalid {
  border-color: var(--leave);
}
textarea.input {
  font-family: var(--wh-font);
  font-weight: 400;
  font-size: 13.5px;
  resize: none;
  min-height: 44px;
}
.select-wrap {
  position: relative;
}
.native-select {
  font-family: var(--wh-font);
  font-weight: 500;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
  padding-right: 34px;
}
.select-caret {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  color: var(--faint);
  pointer-events: none;
}

/* day / week chips */
.daychips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.dchip {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  border: 1px solid var(--line-strong);
  background: var(--surface);
  font-family: var(--wh-font);
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: 0.12s;
}
.dchip.on {
  background: var(--brand);
  color: #fff;
  border-color: transparent;
}

/* segmented control */
.seg {
  display: flex;
  background: var(--surface-3);
  border-radius: var(--r-sm);
  padding: 3px;
  gap: 3px;
}
.seg button {
  flex: 1;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  border: 0;
  background: transparent;
  color: var(--muted);
  padding: 7px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.12s;
}
.seg button.on {
  background: var(--surface);
  color: var(--brand);
  box-shadow: var(--shadow-card);
}

/* footer buttons */
.form-foot {
  display: flex;
  gap: 9px;
}
.qbtn {
  flex: 1;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--line-strong);
  background: var(--surface);
  color: var(--ink-2);
  border-radius: var(--r-sm);
  padding: 9px 10px;
  transition: 0.14s;
}
.qbtn:hover {
  border-color: currentColor;
}
.qbtn.solid {
  background: var(--brand);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 6px 14px -6px color-mix(in srgb, var(--brand) 60%, transparent);
}
.qbtn.solid:hover {
  filter: brightness(1.04);
}
.qbtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
